import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	Button,
	ButtonProps,
	Flex,
	Menu,
	MenuDropdownProps,
	MenuItemProps,
	MenuProps,
	FlexProps,
	FloatingPosition,
} from '@mantine/core';
import { memo } from 'react';
import styled from 'styled-components';
import type { MantineMenuItemProps } from './MantineMenu.types';

type Props = {
	targetName?: string;
	targetContent?: JSX.Element;
	dropdownTopContent?: JSX.Element;
	items?: MantineMenuItemProps[];
	bottomSectionItems?: MantineMenuItemProps[];
	position?: FloatingPosition;
	hideArrow?: boolean;
	capitalizeMenuItem?: boolean;
	menuProps?: MenuProps;
	menuDropdownProps?: MenuDropdownProps;
	targetButtonProps?: ButtonProps;
	dataTestId?: string;
};

const MantineMenu = ({
	targetName,
	targetContent,
	items,
	bottomSectionItems,
	position,
	hideArrow,
	dropdownTopContent,
	capitalizeMenuItem,
	menuProps,
	targetButtonProps,
	menuDropdownProps,
	dataTestId,
}: Props) => {
	return (
		<StyledMenu data-testid={dataTestId} position={position} {...menuProps}>
			<Menu.Target>
				<Button
					variant="ghost"
					rightSection={
						!hideArrow && (
							<Icon variant="caret-down" size="lg" style={{ marginLeft: '0.5rem' }} />
						)
					}
					sx={(theme) => ({
						color: theme.colors.dark[2],
						// The ghost variant hovers blue[0]; the dropdown trigger should
						// hover grey like before. `&&` outranks the variant's :hover.
						'&&:hover, &&:active': {
							backgroundColor: theme.colors.gray[0],
						},
					})}
					{...targetButtonProps}
				>
					{targetContent ? (
						targetContent
					) : (
						<Text variant="p-sm" semiBold>
							{targetName}
						</Text>
					)}
				</Button>
			</Menu.Target>
			<StyledMenuDropdown miw={240} maw={480} {...menuDropdownProps}>
				<StyledOptionsContainer direction="column" gap="0.25rem">
					{dropdownTopContent}
					{items?.map((item, index) => {
						return (
							<StyledMenuItem
								key={item.key + index}
								className={`${item.className || ''} ${item.selected ? 'selected' : ''}`}
								onClick={item.onClick}
								rightSection={item.rightSection}
								$selectedGreyTheme={item.selectedGreyTheme}
								data-testid={item.dataTestId}
								disabled={item.disabled}
							>
								<Flex align="center" gap="0.5rem">
									{item.leftSection}
									<Text variant="p-sm" elipsis capitalize={capitalizeMenuItem}>
										{item.label}
									</Text>
								</Flex>
							</StyledMenuItem>
						);
					})}
				</StyledOptionsContainer>

				{bottomSectionItems?.map((item, index) => {
					return (
						<div key={index}>
							<Menu.Divider
								key={`divider-${index}`}
								sx={{
									margin: '0.25rem',
								}}
							/>

							<StyledMenuItem
								key={item.key}
								leftSection={item.icon}
								onClick={item.onClick}
								data-testid={item.dataTestId}
								disabled={item.disabled}
								$selectedGreyTheme={item.selectedGreyTheme}
								sx={(theme) => ({
									color: item.danger ? theme.colors.red[6] : 'inherit',
								})}
							>
								<Text variant="p-sm">{item.label}</Text>
							</StyledMenuItem>
						</div>
					);
				})}
			</StyledMenuDropdown>
		</StyledMenu>
	);
};

const StyledOptionsContainer = styled(Flex)<FlexProps>`
	max-height: 400px;
	overflow-x: hidden;
	overflow-y: auto;

	::-webkit-scrollbar {
		width: 0.125em;
		height: 0.5em;
	}
`;

const StyledMenu = styled(Menu)<MenuProps>`
	color: inherit !important;

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	}

	&[data-expanded='true'],
	&:active {
		background-color: ${({ theme }) => theme.v3.colors.gray[0]};
		color: inherit;
	}
`;

const StyledMenuDropdown = styled(Menu.Dropdown)<MenuDropdownProps>`
	width: max-content;
	border-radius: 0.5rem;
	padding: 0.5rem;
	box-shadow: 0px 16px 32px 0px #dcdfe0;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[0]};
`;

const StyledMenuItem = styled(Menu.Item)<
	MenuItemProps & { disabled?: boolean; onClick?: () => void; $selectedGreyTheme?: boolean }
>`
	/* && bumps specificity so the padding beats v7's unlayered .mantine-Menu-item
	   base rule regardless of stylesheet injection order. */
	&& {
		padding: 0.5rem;
	}

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	}

	&.selected {
		border: 1px solid
			${({ theme, $selectedGreyTheme }) =>
				$selectedGreyTheme ? 'transparent' : theme.v3.colors.blue[6]};
		background: ${({ theme, $selectedGreyTheme }) =>
			$selectedGreyTheme ? theme.v3.colors.gray[0] : theme.v3.colors.blue[0]};

		.mantine-Menu-itemSection[data-position='right'] {
			color: ${({ theme, $selectedGreyTheme }) =>
				$selectedGreyTheme ? '' : theme.v3.colors.blue[6]};
		}
	}
`;

const Memoized = memo(MantineMenu);

export { Memoized as MantineMenu };
